import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PropertyListView } from "@/components/public/PropertyListView";
import { useI18n } from "@/lib/i18n";
import { fetchPublicProperties } from "@/lib/publicData";
import { findProjectBySlug } from "@/lib/projects";
import { buildHead, pickLang } from "@/lib/seo";

export const Route = createFileRoute("/_public/proyectos/$slug")({
  loader: async ({ params }) => {
    const project = await findProjectBySlug(params.slug);
    if (!project) throw notFound();
    const properties = await fetchPublicProperties({ project_name: project.name });
    return { project, properties };
  },
  head: ({ params, match, loaderData }) => {
    const lang = pickLang(match.search);
    const name = loaderData?.project.name ?? params.slug;
    const title = `${name} — Unidades disponibles | Dahiana Marín Bienes Raíces`;
    const description = `Unidades disponibles en ${name}: superficies, tipologías y precios actualizados. Consultá disponibilidad y plan de pagos.`;
    return buildHead({ lang, path: `/proyectos/${params.slug}`, title, description });
  },
  errorComponent: () => <ProjectMissing />,
  notFoundComponent: () => <ProjectMissing />,
  component: ProjectDetail,
});

function ProjectMissing() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-serif text-3xl text-foreground">Proyecto no disponible</h1>
      <p className="mt-3 text-muted-foreground">
        No encontramos unidades para este proyecto.{" "}
        <Link to="/proyectos" className="underline">
          Ver todos los proyectos
        </Link>
        .
      </p>
    </div>
  );
}

function ProjectDetail() {
  const { project, properties } = Route.useLoaderData();
  const { lang } = useI18n();

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <nav className="text-xs uppercase tracking-wider text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {lang === "en" ? "Home" : lang === "pt" ? "Início" : "Inicio"}
        </Link>
        <span className="mx-2">·</span>
        <Link to="/proyectos" className="hover:text-foreground">
          {lang === "en" ? "Projects" : lang === "pt" ? "Projetos" : "Proyectos"}
        </Link>
        <span className="mx-2">·</span>
        <span>{project.name}</span>
      </nav>

      <h1 className="mt-3 text-3xl font-semibold text-foreground md:text-4xl">{project.name}</h1>
      <p className="mt-2 text-sm text-muted-foreground md:text-base">
        {project.units}{" "}
        {lang === "en" ? "units published in this project." : lang === "pt" ? "unidades publicadas neste projeto." : "unidades publicadas en este proyecto."}
      </p>

      <div className="mt-10">
        <PropertyListView filters={{ project_name: project.name }} initialProperties={properties as never} />
      </div>
    </div>
  );
}
